import { useState, useEffect, useRef } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { 
  ArrowLeft, Package, MapPin, Clock, CheckCircle, XCircle, Truck, 
  CreditCard, Phone, Mail, Calendar, DollarSign, RotateCcw, X,
  AlertCircle, Info
} from 'lucide-react'
import { api } from '../lib/api'
import { useAuth } from '../contexts/AuthContext'
import { useCart } from '../contexts/CartContext'
import toast from 'react-hot-toast'
import { Order } from '../types'
import ProductImage from '../components/ProductImage'
import StoreImage from '../components/StoreImage'
import { getCache, getCacheStale, setCache } from '../lib/cache'

const OrderDetails = () => {
  const { orderId } = useParams<{ orderId: string }>()
  const navigate = useNavigate()
  const { isAuthenticated, user } = useAuth()
  const { addItem, clearCart } = useCart()
  const [order, setOrder] = useState<Order | null>(null)
  const [loading, setLoading] = useState(true)
  const [cancelling, setCancelling] = useState(false)
  const [reordering, setReordering] = useState(false)

  // Ref para prevenir ejecuciones duplicadas (React StrictMode)
  const loadingOrderRef = useRef(false)

  useEffect(() => {
    if (!isAuthenticated || !orderId) return
    
    // Prevenir ejecuciones duplicadas causadas por React StrictMode
    if (loadingOrderRef.current) return
    loadingOrderRef.current = true

    loadOrder().finally(() => {
      loadingOrderRef.current = false
    })
  }, [isAuthenticated, orderId, user?.id])

  const loadOrder = async () => {
    if (!orderId) return
    
    const cacheKey = `order_details_${orderId}`
    
    // Intentar cargar desde cache primero
    const cachedOrder = getCacheStale<Order>(cacheKey)
    if (cachedOrder) {
      setOrder(cachedOrder)
      setLoading(false)
      // Si tenemos cache válido, no hacer query HTTP (evitar 304 innecesario)
      // Solo hacer query si el cache está expirado o no existe
      return
    }

    // OPTIMIZACIÓN: Verificar si order ya está en OrderHistory cache
    if (user?.id) {
      const ordersCacheKey = `orders_history_${user.id}`
      const cachedOrders = getCacheStale<Order[]>(ordersCacheKey)
      if (cachedOrders && cachedOrders.length > 0) {
        const orderFromHistory = cachedOrders.find((o: Order) => o.id === orderId)
        if (orderFromHistory) {
          setOrder(orderFromHistory)
          setLoading(false)
          // Guardar también en cache específico de order detail
          setCache(cacheKey, orderFromHistory, 2 * 60 * 1000)
          return
        }
      }
    }

    try {
      setLoading(true)
      const response = await api.getOrder(orderId!)
      if (response.success && response.data) {
        // Normalizar los datos de la orden
        const orderData = response.data
        const normalizedOrder: Order = {
          id: orderData.id,
          storeId: orderData.store_id || orderData.storeId,
          customerName: orderData.customer_name || orderData.customerName,
          customerPhone: orderData.customer_phone || orderData.customerPhone,
          customerEmail: orderData.customer_email || orderData.customerEmail,
          status: orderData.status,
          totalAmount: Number(orderData.total_amount || orderData.totalAmount) || 0,
          deliveryFee: Number(orderData.delivery_fee || orderData.deliveryFee) || 0,
          taxAmount: Number(orderData.tax_amount || orderData.taxAmount) || 0,
          estimatedDeliveryTime: Number(orderData.estimated_delivery_time || orderData.estimatedDeliveryTime) || 0,
          notes: orderData.notes,
          paymentMethod: orderData.payment_method || orderData.paymentMethod,
          paymentStatus: orderData.payment_status || orderData.paymentStatus,
          createdAt: orderData.created_at || orderData.createdAt,
          items: (orderData.items || []).map((item: any) => ({
            id: item.id,
            productId: item.product_id || item.productId,
            variantId: item.variant_id || item.variantId,
            quantity: Number(item.quantity) || 0,
            unitPrice: Number(item.unit_price || item.unitPrice) || 0,
            notes: item.notes,
            variant: item.variant ? {
              id: item.variant.id,
              variant_name: item.variant.variant_name,
              variant_type: item.variant.variant_type,
              stock_quantity: Number(item.variant.stock_quantity) || 0,
              is_available: item.variant.is_available !== false,
              sku: item.variant.sku,
              image_url: item.variant.image_url
            } : undefined,
            product: item.product ? {
              id: item.product.id || item.productId,
              name: item.product.name || 'Producto',
              description: item.product.description,
              price: Number(item.product.price || item.unitPrice) || 0,
              imageUrl: item.product.image_url || item.product.imageUrl,
              categoryId: item.product.category_id || '',
              storeId: orderData.store_id || orderData.storeId,
              isAvailable: item.product.is_available !== false,
              stockQuantity: Number(item.product.stock_quantity || 999) || 999,
              unit: item.product.unit || 'unidad',
              isFeatured: false
            } : undefined
          })),
          store: orderData.store ? {
            id: orderData.store_id || orderData.storeId,
            name: orderData.store.name || 'Negocio',
            slug: orderData.store.slug || '',
            address: orderData.store.address || '',
            phone: orderData.store.phone,
            email: '',
            latitude: 0,
            longitude: 0,
            status: 'approved' as const,
            isActive: true,
            offersKitchenService: false,
            offersDelivery: true,
            deliveryFee: Number(orderData.delivery_fee || orderData.deliveryFee) || 0,
            minOrderAmount: 0,
            ownerId: ''
          } : undefined
        }
        setOrder(normalizedOrder)
        
        // Guardar en cache (2 minutos de TTL - las órdenes cambian frecuentemente)
        setCache(cacheKey, normalizedOrder, 2 * 60 * 1000)
      } else {
        toast.error('Pedido no encontrado')
        navigate('/orders')
      }
    } catch (error: any) {
      console.error('Error loading order:', error)
      
      // Si hay error de red y tenemos datos en cache, mantenerlos
      if ((error.code === 'ERR_NETWORK' || error.code === 'ECONNABORTED' || !error.response) && cachedOrder) {
        // Ya tenemos los datos del cache, no hacer nada más
        return
      }
      
      toast.error(error.response?.data?.message || 'Error al cargar el pedido')
      navigate('/orders')
    } finally {
      setLoading(false)
    }
  }

  const handleCancelOrder = async () => {
    if (!order) return

    if (!confirm('¿Estás seguro de cancelar este pedido?')) {
      return
    }

    try {
      setCancelling(true)
      const response = await api.cancelOrder(order.id)
      if (response.success) {
        toast.success('Pedido cancelado exitosamente')
        loadOrder() // Reload to get updated status
      }
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Error al cancelar el pedido')
    } finally {
      setCancelling(false)
    }
  }

  const handleReorder = async () => {
    if (!order || !order.store) return

    try {
      setReordering(true)
      
      // Clear current cart
      clearCart()
      
      // Add all items from the order (addItem will automatically handle the store)
      for (const item of order.items) {
        if (item.product) {
          addItem(item.product, order.store, item.quantity, item.notes)
        }
      }
      
      toast.success('Productos agregados al carrito')
      navigate(`/stores/${order.store.id}/catalog`)
    } catch (error) {
      toast.error('Error al reordenar')
    } finally {
      setReordering(false)
    }
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200'
      case 'preparing':
        return 'bg-blue-100 text-blue-800 border-blue-200'
      case 'ready':
        return 'bg-purple-100 text-purple-800 border-purple-200'
      case 'delivering':
        return 'bg-orange-100 text-orange-800 border-orange-200'
      case 'delivered':
        return 'bg-green-100 text-green-800 border-green-200'
      case 'cancelled':
        return 'bg-red-100 text-red-800 border-red-200'
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200'
    }
  }

  const getStatusText = (status: string) => {
    switch (status) {
      case 'pending':
        return 'Pendiente'
      case 'preparing':
        return 'Preparando'
      case 'ready':
        return 'Listo para Entrega'
      case 'delivering':
        return 'En Camino'
      case 'delivered':
        return 'Entregado'
      case 'cancelled':
        return 'Cancelado'
      default:
        return status
    }
  }

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'pending':
      case 'preparing':
        return <Clock className="w-5 h-5 sm:w-6 sm:h-6" />
      case 'ready':
        return <Package className="w-5 h-5 sm:w-6 sm:h-6" />
      case 'delivering':
        return <Truck className="w-5 h-5 sm:w-6 sm:h-6" />
      case 'delivered':
        return <CheckCircle className="w-5 h-5 sm:w-6 sm:h-6" />
      case 'cancelled':
        return <XCircle className="w-5 h-5 sm:w-6 sm:h-6" />
      default:
        return <Package className="w-5 h-5 sm:w-6 sm:h-6" />
    }
  }

  const canCancel = order && (order.status === 'pending' || order.status === 'preparing')
  const canReorder = order && order.status !== 'cancelled' && order.store

  if (!isAuthenticated) {
    return null
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center pb-20">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-4 text-gray-600 font-medium">Cargando pedido...</p>
        </div>
      </div>
    )
  }

  if (!order) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center pb-20">
        <div className="text-center max-w-md w-full px-4">
          <div className="bg-white rounded-3xl shadow-xl p-8 sm:p-12">
            <div className="w-24 h-24 mx-auto mb-6 bg-gradient-to-br from-red-100 to-red-200 rounded-full flex items-center justify-center">
              <XCircle className="w-12 h-12 text-red-600" />
            </div>
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-3">Pedido no encontrado</h2>
            <p className="text-gray-600 mb-8 text-sm sm:text-base">El pedido que buscas no existe o no tienes acceso a él</p>
            <button
              onClick={() => navigate('/orders')}
              className="w-full sm:w-auto px-8 py-4 bg-gradient-to-r from-primary-600 to-primary-700 text-white rounded-2xl hover:from-primary-700 hover:to-primary-800 font-semibold shadow-lg hover:shadow-xl transition-all duration-300 active:scale-95"
            >
              Volver a Mis Pedidos
            </button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-50 pb-20 sm:pb-6">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-6 lg:py-8">
        {/* Header */}
        <div className="bg-white/80 backdrop-blur-xl rounded-2xl sm:rounded-3xl shadow-sm border border-gray-100 p-4 sm:p-6 mb-4 sm:mb-6 sticky top-0 z-10">
          <div className="flex items-center gap-3 sm:gap-4">
            <button
              onClick={() => navigate('/orders')}
              className="p-2 sm:p-2.5 hover:bg-gray-100 active:bg-gray-200 rounded-xl sm:rounded-2xl transition-all duration-200 active:scale-95"
            >
              <ArrowLeft className="w-5 h-5 sm:w-6 sm:h-6 text-gray-700" />
            </button>
            <div className="flex-1">
              <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold text-gray-900">Detalles del Pedido</h1>
              <p className="text-xs sm:text-sm text-gray-600 mt-0.5">#{order.id.slice(0, 8)}</p>
            </div>
          </div>
        </div>

        {/* Status Card */}
        <div className="bg-white rounded-2xl sm:rounded-3xl shadow-sm border border-gray-100 p-5 sm:p-6 lg:p-7 mb-4 sm:mb-6">
          <div className="flex items-center justify-between mb-4 sm:mb-5">
            <div className="flex items-center gap-3 sm:gap-4">
              <div className={`w-12 h-12 sm:w-14 sm:h-14 rounded-xl sm:rounded-2xl flex items-center justify-center border-2 ${getStatusColor(order.status)}`}>
                {getStatusIcon(order.status)}
              </div>
              <div>
                <h2 className="text-lg sm:text-xl lg:text-2xl font-bold text-gray-900">Estado del Pedido</h2>
                <p className={`text-sm sm:text-base font-semibold ${getStatusColor(order.status).split(' ')[1]}`}>
                  {getStatusText(order.status)}
                </p>
              </div>
            </div>
            {order.estimatedDeliveryTime && order.status !== 'delivered' && order.status !== 'cancelled' && (
              <div className="text-right">
                <p className="text-xs sm:text-sm text-gray-500">Tiempo estimado</p>
                <p className="text-base sm:text-lg font-bold text-gray-900">{order.estimatedDeliveryTime} min</p>
              </div>
            )}
          </div>

          {/* Status Timeline */}
          <div className="space-y-3 sm:space-y-4">
            {['pending', 'preparing', 'ready', 'delivering', 'delivered'].map((status, index) => {
              const isActive = order.status === status
              const isCompleted = ['pending', 'preparing', 'ready', 'delivering', 'delivered'].indexOf(order.status) > index
              const isCancelled = order.status === 'cancelled'

              return (
                <div key={status} className="flex items-center gap-3 sm:gap-4">
                  <div className={`w-8 h-8 sm:w-10 sm:h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
                    isCancelled ? 'bg-gray-100 border-2 border-gray-300' :
                    isCompleted ? 'bg-green-500 text-white' :
                    isActive ? 'bg-primary-600 text-white ring-4 ring-primary-100' :
                    'bg-gray-100 border-2 border-gray-300 text-gray-400'
                  }`}>
                    {isCompleted ? (
                      <CheckCircle className="w-5 h-5 sm:w-6 sm:h-6" />
                    ) : isActive ? (
                      <Clock className="w-5 h-5 sm:w-6 sm:h-6" />
                    ) : (
                      <div className="w-3 h-3 sm:w-4 sm:h-4 rounded-full bg-current" />
                    )}
                  </div>
                  <div className="flex-1">
                    <p className={`text-sm sm:text-base font-semibold ${
                      isActive ? 'text-primary-600' : isCompleted ? 'text-green-600' : 'text-gray-500'
                    }`}>
                      {getStatusText(status)}
                    </p>
                    {isActive && (
                      <p className="text-xs sm:text-sm text-gray-500 mt-0.5">En progreso</p>
                    )}
                  </div>
                </div>
              )
            })}
          </div>
        </div>

        {/* Store Info */}
        {order.store && (
          <div className="bg-white rounded-2xl sm:rounded-3xl shadow-sm border border-gray-100 p-5 sm:p-6 lg:p-7 mb-4 sm:mb-6">
            <h2 className="text-lg sm:text-xl lg:text-2xl font-bold text-gray-900 mb-4 sm:mb-5 flex items-center gap-2 sm:gap-3">
              <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-primary-100 to-primary-200 rounded-xl sm:rounded-2xl flex items-center justify-center">
                <Package className="w-5 h-5 sm:w-6 sm:h-6 text-primary-600" />
              </div>
              Negocio
            </h2>
            <div className="flex items-center gap-4 sm:gap-5">
              <div className="w-16 h-16 sm:w-20 sm:h-20 flex-shrink-0 bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl sm:rounded-2xl overflow-hidden shadow-sm">
                <StoreImage
                  imageKey={order.store.logoUrl || null}
                  type="logo"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="flex-1">
                <h3 className="font-bold text-gray-900 text-base sm:text-lg lg:text-xl mb-1">{order.store.name}</h3>
                <p className="text-sm sm:text-base text-gray-600">{order.store.address}</p>
                {order.store.phone && (
                  <p className="text-sm sm:text-base text-gray-600 flex items-center gap-2 mt-1">
                    <Phone className="w-4 h-4" />
                    {order.store.phone}
                  </p>
                )}
              </div>
              <button
                onClick={() => navigate(`/stores/${order.storeId}/catalog`)}
                className="px-4 sm:px-5 py-2 sm:py-2.5 text-primary-600 hover:bg-primary-50 active:bg-primary-100 rounded-xl sm:rounded-2xl font-semibold text-sm sm:text-base transition-all duration-200 active:scale-95"
              >
                Ver Negocio
              </button>
            </div>
          </div>
        )}

        {/* Order Items */}
        <div className="bg-white rounded-2xl sm:rounded-3xl shadow-sm border border-gray-100 p-5 sm:p-6 lg:p-7 mb-4 sm:mb-6">
          <h2 className="text-lg sm:text-xl lg:text-2xl font-bold text-gray-900 mb-4 sm:mb-5">Productos</h2>
          <div className="space-y-3 sm:space-y-4">
            {order.items.map((item) => (
              <div
                key={item.id}
                className="flex gap-3 sm:gap-4 p-3 sm:p-4 bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl sm:rounded-2xl border border-gray-200"
              >
                <div className="w-16 h-16 sm:w-20 sm:h-20 flex-shrink-0 bg-white rounded-xl sm:rounded-2xl overflow-hidden shadow-sm">
                  <ProductImage
                    imageKey={item.variant?.image_url || item.product?.imageUrl || null}
                    alt={item.product?.name || 'Producto'}
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="font-bold text-gray-900 text-sm sm:text-base lg:text-lg mb-1 line-clamp-2">
                    {item.product?.name || 'Producto'}
                    {item.variant && (
                      <span className="text-primary-600 font-normal ml-2">
                        - {item.variant.variant_name}
                      </span>
                    )}
                  </h3>
                  {item.product?.description && (
                    <p className="text-xs sm:text-sm text-gray-600 line-clamp-1 mb-2">
                      {item.product.description}
                    </p>
                  )}
                  <div className="flex items-center justify-between">
                    <span className="text-xs sm:text-sm text-gray-500">
                      Cantidad: {item.quantity} {item.product?.unit || 'unidad'}
                    </span>
                    <span className="text-sm sm:text-base lg:text-lg font-bold text-primary-600">
                      RD${(item.quantity * (Number(item.unitPrice) || 0)).toFixed(2)}
                    </span>
                  </div>
                  {item.notes && (
                    <p className="text-xs sm:text-sm text-gray-500 mt-2 italic">
                      Nota: {item.notes}
                    </p>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Delivery Address */}
        {order.store && (
          <div className="bg-white rounded-2xl sm:rounded-3xl shadow-sm border border-gray-100 p-5 sm:p-6 lg:p-7 mb-4 sm:mb-6">
            <h2 className="text-lg sm:text-xl lg:text-2xl font-bold text-gray-900 mb-4 sm:mb-5 flex items-center gap-2 sm:gap-3">
              <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-blue-100 to-blue-200 rounded-xl sm:rounded-2xl flex items-center justify-center">
                <MapPin className="w-5 h-5 sm:w-6 sm:h-6 text-blue-600" />
              </div>
              Dirección de Entrega
            </h2>
            <div className="p-4 sm:p-5 bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl sm:rounded-2xl border border-gray-200">
              <p className="font-semibold text-gray-900 text-sm sm:text-base mb-1">{order.customerName}</p>
              {order.customerPhone && (
                <p className="text-sm sm:text-base text-gray-600 flex items-center gap-2 mb-1">
                  <Phone className="w-4 h-4" />
                  {order.customerPhone}
                </p>
              )}
              {order.customerEmail && (
                <p className="text-sm sm:text-base text-gray-600 flex items-center gap-2 mb-2">
                  <Mail className="w-4 h-4" />
                  {order.customerEmail}
                </p>
              )}
              <p className="text-sm sm:text-base text-gray-700 mt-2">
                {order.store.address}
              </p>
            </div>
          </div>
        )}

        {/* Payment & Order Info */}
        <div className="bg-white rounded-2xl sm:rounded-3xl shadow-sm border border-gray-100 p-5 sm:p-6 lg:p-7 mb-4 sm:mb-6">
          <h2 className="text-lg sm:text-xl lg:text-2xl font-bold text-gray-900 mb-4 sm:mb-5 flex items-center gap-2 sm:gap-3">
            <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-green-100 to-green-200 rounded-xl sm:rounded-2xl flex items-center justify-center">
              <CreditCard className="w-5 h-5 sm:w-6 sm:h-6 text-green-600" />
            </div>
            Información de Pago
          </h2>
          <div className="space-y-3 sm:space-y-4">
            <div className="flex justify-between text-sm sm:text-base">
              <span className="text-gray-600">Método de Pago</span>
              <span className="font-semibold text-gray-900">
                {order.paymentMethod === 'cash' ? 'Efectivo' : order.paymentMethod === 'card' ? 'Tarjeta' : order.paymentMethod || 'N/A'}
              </span>
            </div>
            <div className="flex justify-between text-sm sm:text-base">
              <span className="text-gray-600">Estado de Pago</span>
              <span className={`font-semibold ${
                order.paymentStatus === 'paid' ? 'text-green-600' :
                order.paymentStatus === 'pending' ? 'text-yellow-600' :
                'text-gray-600'
              }`}>
                {order.paymentStatus === 'paid' ? 'Pagado' : order.paymentStatus === 'pending' ? 'Pendiente' : order.paymentStatus || 'N/A'}
              </span>
            </div>
            <div className="flex justify-between text-sm sm:text-base">
              <span className="text-gray-600 flex items-center gap-2">
                <Calendar className="w-4 h-4" />
                Fecha del Pedido
              </span>
              <span className="font-semibold text-gray-900">
                {new Date(order.createdAt).toLocaleDateString('es-ES', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit'
                })}
              </span>
            </div>
            {order.notes && (
              <div className="pt-3 sm:pt-4 border-t border-gray-200">
                <p className="text-xs sm:text-sm text-gray-500 mb-1 flex items-center gap-2">
                  <Info className="w-4 h-4" />
                  Notas del Pedido
                </p>
                <p className="text-sm sm:text-base text-gray-700">{order.notes}</p>
              </div>
            )}
          </div>
        </div>

        {/* Order Summary */}
        <div className="bg-white rounded-2xl sm:rounded-3xl shadow-lg border border-gray-100 p-5 sm:p-6 lg:p-7 mb-4 sm:mb-6">
          <h2 className="text-lg sm:text-xl lg:text-2xl font-bold text-gray-900 mb-4 sm:mb-5 flex items-center gap-2 sm:gap-3">
            <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-purple-100 to-purple-200 rounded-xl sm:rounded-2xl flex items-center justify-center">
              <DollarSign className="w-5 h-5 sm:w-6 sm:h-6 text-purple-600" />
            </div>
            Resumen del Pedido
          </h2>
          <div className="space-y-3 sm:space-y-4">
            <div className="flex justify-between text-sm sm:text-base">
              <span className="text-gray-600">Subtotal</span>
              <span className="font-semibold text-gray-900">
                RD${((Number(order.totalAmount) || 0) - (Number(order.deliveryFee) || 0) - (Number(order.taxAmount) || 0)).toFixed(2)}
              </span>
            </div>
            <div className="flex justify-between text-sm sm:text-base">
              <span className="text-gray-600">Entrega</span>
              <span className="font-semibold text-gray-900">RD${(Number(order.deliveryFee) || 0).toFixed(2)}</span>
            </div>
            <div className="flex justify-between text-sm sm:text-base">
              <span className="text-gray-600">ITBIS (18%)</span>
              <span className="font-semibold text-gray-900">RD${(Number(order.taxAmount) || 0).toFixed(2)}</span>
            </div>
            <div className="border-t-2 border-gray-200 pt-4 sm:pt-5 flex justify-between">
              <span className="font-bold text-gray-900 text-base sm:text-lg">Total</span>
              <span className="font-bold text-primary-600 text-xl sm:text-2xl lg:text-3xl">
                RD${(Number(order.totalAmount) || 0).toFixed(2)}
              </span>
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
          {canCancel && (
            <button
              onClick={handleCancelOrder}
              disabled={cancelling}
              className="flex-1 px-6 py-4 sm:py-5 bg-gradient-to-r from-red-600 to-red-700 text-white rounded-xl sm:rounded-2xl hover:from-red-700 hover:to-red-800 font-bold text-sm sm:text-base lg:text-lg shadow-lg hover:shadow-xl transition-all duration-300 active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 sm:gap-3"
            >
              <X className="w-5 h-5 sm:w-6 sm:h-6" />
              {cancelling ? 'Cancelando...' : 'Cancelar Pedido'}
            </button>
          )}
          {canReorder && (
            <button
              onClick={handleReorder}
              disabled={reordering}
              className="flex-1 px-6 py-4 sm:py-5 bg-gradient-to-r from-primary-600 to-primary-700 text-white rounded-xl sm:rounded-2xl hover:from-primary-700 hover:to-primary-800 font-bold text-sm sm:text-base lg:text-lg shadow-lg hover:shadow-xl transition-all duration-300 active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 sm:gap-3"
            >
              <RotateCcw className="w-5 h-5 sm:w-6 sm:h-6" />
              {reordering ? 'Agregando...' : 'Reordenar'}
            </button>
          )}
        </div>

        {/* Cancellation Info */}
        {order.status === 'cancelled' && (
          <div className="mt-4 sm:mt-6 bg-red-50 border-2 border-red-200 rounded-xl sm:rounded-2xl p-4 sm:p-5">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-5 h-5 sm:w-6 sm:h-6 text-red-600 flex-shrink-0 mt-0.5" />
              <div>
                <h3 className="font-bold text-red-900 text-sm sm:text-base mb-1">Pedido Cancelado</h3>
                <p className="text-xs sm:text-sm text-red-700">
                  Este pedido ha sido cancelado. Si tienes alguna pregunta, contacta al negocio.
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default OrderDetails

